/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import {
  Badge, Box, Button, Callout, Card, Checkbox, Flex, Grid, Heading, IconButton, Table, Tabs, Text, TextField,
  VisuallyHidden,
} from '@radix-ui/themes';
import {
  ArrowUp, Download, ExternalLink, Info, RefreshCw, Search,
} from 'lucide-react';

import '../quarantine/QuarantinePreviewView.scss';
import { ExamplePagination } from '../ExamplePagination';
import { ExampleBadge, ExampleDataCallout, ExampleFrame, UpgradeCallout } from '../quarantine/ExampleFrame';
import {
  MALWARE_AUDIT_FIXTURE, MALWARE_EVENT_FILTERS, MALWARE_EVENT_FIXTURE, MALWARE_STATS,
} from './malwareFixtures';

interface StatCardProps {
  label: string;
  value: number;
  caption: string;
  accent?: boolean;
}

function StatCard({ label, value, caption, accent = false }: StatCardProps): JSX.Element {
  return (
    <Card size="3" style={accent ? { borderLeft: '4px solid var(--red-9)' } : undefined}>
      <Flex direction="column" gap="2">
        <Text size="2" color={accent ? undefined : 'gray'}>
          {label}
        </Text>
        <Heading as="h3" size="7" color={accent ? 'red' : 'gray'} style={{ lineHeight: 1, margin: 0 }}>
          {value}
        </Heading>
        <Text size="1" color="gray">
          {caption}
        </Text>
      </Flex>
    </Card>
  );
}

function MaliciousPackagesTab(): JSX.Element {
  const audit = MALWARE_AUDIT_FIXTURE;
  return (
    <Flex direction="column" gap="4" pt="4">
      <ExampleDataCallout />
      <UpgradeCallout>to quarantine malicious packages before your developers consume them.</UpgradeCallout>
      <Grid columns="3" gap="4">
        <StatCard
          label="Malicious Packages"
          value={MALWARE_STATS.malicious}
          caption="Currently In This Repository"
          accent
        />
        <StatCard label="Malicious Packages Blocked" value={MALWARE_STATS.blocked} caption="Last 30 days" />
        <StatCard label="Package Requests Protected" value={MALWARE_STATS.protectedRequests} caption="Last 30 days" />
      </Grid>
      <ExampleFrame label="Example audit" bordered={false}>
        <Flex align="center" justify="between" pb="3">
          <Flex align="center" gap="2">
            <Heading as="h2" size="5">
              Malicious Package Audit
            </Heading>
            <ExampleBadge />
          </Flex>
          <Button variant="outline" color="gray" disabled>
            Run Audit Now
          </Button>
        </Flex>
        <Box style={{ border: '1px solid var(--gray-a6)', borderRadius: 'var(--radius-3)', overflow: 'hidden' }}>
          <Table.Root variant="ghost" size="2">
            <caption>
              <VisuallyHidden>Example malware audits</VisuallyHidden>
            </caption>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  <Flex align="center" gap="1">
                    Audit Run
                    <ArrowUp size={14} aria-hidden="true" />
                  </Flex>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="center">Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Summary</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="center">Affected Packages</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="center">Affected Repositories</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>
                  <Flex align="center" gap="2">
                    <Text size="2" color="blue">
                      {audit.auditRun}
                    </Text>
                    <ExampleBadge />
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2" color="gray">
                    {audit.type}
                  </Text>
                </Table.Cell>
                <Table.Cell align="center">
                  <Badge color="red" variant="solid" radius="small" size="1">
                    Unresolved
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2" color="blue">
                    {audit.summary}
                  </Text>
                </Table.Cell>
                <Table.Cell align="center">
                  <Text size="2">{audit.affectedPackages}</Text>
                </Table.Cell>
                <Table.Cell align="center">
                  <Text size="2">{audit.affectedRepositories}</Text>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Box>
        <ExamplePagination rangeLabel="1-25 of 26" pageCount={2} />
      </ExampleFrame>
    </Flex>
  );
}

function EventLogTab(): JSX.Element {
  const event = MALWARE_EVENT_FIXTURE;
  return (
    <Flex direction="column" gap="4" pt="4">
      <ExampleDataCallout />
      <Callout.Root color="gray" variant="soft" size="2">
        <Callout.Icon>
          <Info size={16} aria-hidden="true" />
        </Callout.Icon>
        <Callout.Text>
          The event log records security activity across your repositories — when a malicious or policy-violating
          component was requested, which repository it came through, and how it was resolved.
        </Callout.Text>
      </Callout.Root>
      <UpgradeCallout>to unlock event logging for malware.</UpgradeCallout>
      <Flex align="center" gap="2">
        <Heading as="h2" size="5">
          Event Log
        </Heading>
        <ExampleBadge />
      </Flex>
      <ExampleFrame label="Example event" bordered={false}>
        <Grid columns={{ initial: '1', md: '250px 1fr' }} gap="6">
          <Box>
            <Button variant="outline" color="gray" disabled>
              <RefreshCw size={12} aria-hidden="true" />
              Reset filters
            </Button>
            <Flex direction="column" gap="4" mt="3">
              {MALWARE_EVENT_FILTERS.map((group) => (
                <Box key={group.title} role="group" aria-label={group.title}>
                  <Text as="div" size="2" weight="bold" mb="3">
                    {group.title}
                  </Text>
                  <Flex direction="column" gap="1">
                    {group.options.map((option) => (
                      <Text as="label" size="2" key={option.label}>
                        <Flex align="center" gap="2">
                          <Checkbox disabled />
                          {option.label}
                          <Badge color="gray" variant="soft" radius="full" size="1">
                            {option.count}
                          </Badge>
                        </Flex>
                      </Text>
                    ))}
                  </Flex>
                </Box>
              ))}
            </Flex>
          </Box>
          <Flex direction="column" gap="4">
            <Flex align="center" gap="3" wrap="wrap">
              <Box style={{ flex: '1 1 240px', minWidth: 0 }}>
                <TextField.Root placeholder="Search events" aria-label="Search events" disabled>
                  <TextField.Slot>
                    <Search size={16} aria-hidden="true" />
                  </TextField.Slot>
                </TextField.Root>
              </Box>
              <Button variant="outline" color="gray" disabled aria-label="Download event log as CSV">
                <Download size={14} aria-hidden="true" />
                CSV
              </Button>
            </Flex>
            <Box style={{ border: '1px solid var(--gray-a6)', borderRadius: 'var(--radius-3)', overflow: 'hidden' }}>
              <Table.Root variant="ghost" size="2">
                <caption>
                  <VisuallyHidden>Example malware events</VisuallyHidden>
                </caption>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Package</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Repository</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>CVE</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Discovered</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Resolved</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Resolved By</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>
                      <Flex align="center" gap="2">
                        <Text size="2">{event.packageName}</Text>
                        <ExampleBadge />
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2">{event.repository}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex asChild align="center" gap="1" display="inline-flex">
                        <a href={event.cveUrl} target="_blank" rel="noopener noreferrer">
                          <Text size="2">{event.cve}</Text>
                          <ExternalLink size={14} aria-hidden="true" style={{ flexShrink: 0 }} />
                        </a>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2" color="blue">
                        {event.discovered}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color="gray" variant="soft" size="1">
                        {event.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2" color="blue">
                        {event.resolved}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2">{event.resolvedBy}</Text>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </Box>
          </Flex>
        </Grid>
      </ExampleFrame>
    </Flex>
  );
}

/** Preview tier makes no network call, which is what makes the tab reviewable with no backend. */
export function MalwarePreviewView(): JSX.Element {
  return (
    <Box className="firewall-security-preview" data-testid="malware-preview">
      <Tabs.Root defaultValue="malicious-packages">
        <Tabs.List>
          <Tabs.Trigger value="malicious-packages">Malicious Packages</Tabs.Trigger>
          <Tabs.Trigger value="event-log">Event Log</Tabs.Trigger>
          {/* Static: the preview has nothing to refresh. */}
          <Flex align="center" gap="2" ml="auto" pb="1">
            <Text size="2" color="gray">
              Data from 2 min ago
            </Text>
            <IconButton variant="outline" color="gray" size="2" disabled aria-label="Refresh data">
              <RefreshCw size={14} aria-hidden="true" />
            </IconButton>
          </Flex>
        </Tabs.List>
        <Tabs.Content value="malicious-packages">
          <MaliciousPackagesTab />
        </Tabs.Content>
        <Tabs.Content value="event-log">
          <EventLogTab />
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  );
}
